// src/components/mobile/MobileBottomNav.js
//
// Revolut-style mobile bottom nav with a raised center FAB pointing at
// the teacher's primary verb (log a wird / تسميع). On non-teacher roles
// (parent / student / admin) the items array is passed in by Layout
// and the FAB target is configurable, so this same component drives
// every role's mobile bottom bar.
//
// The component renders 5 slots. The middle slot is rendered as a
// floating FAB (-22px above the bar) with a gradient bg and glow. The
// other 4 slots use compact icon+label buttons.

import React from 'react';
import { Box, IconButton, Paper, Typography, useTheme } from '@mui/material';

const MOBILE_BAR_HEIGHT = 64;
const FAB_SIZE = 60;

const NavItem = ({ icon, label, active, onClick }) => (
  <IconButton
    onClick={onClick}
    disableRipple
    sx={(t) => ({
      flex: 1,
      position: 'relative',
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25,
      borderRadius: 1,
      py: 0.75,
      color: active ? t.palette.sacred.primary : t.palette.sacred.onSurfaceVariant,
      transition: 'color 200ms cubic-bezier(0.16,1,0.3,1), transform 120ms',
      '& svg': {
        fontSize: 24,
        transition: 'transform 220ms cubic-bezier(0.16,1,0.3,1)',
        transform: active ? 'translateY(-1px) scale(1.05)' : 'none',
      },
      '&:hover': { backgroundColor: 'transparent' },
      '&:active': { transform: 'scale(0.94)' },
    })}
  >
    {/* Active-state dot indicator above icon */}
    <Box
      aria-hidden
      sx={(t) => ({
        position: 'absolute',
        top: 2,
        width: 4, height: 4,
        borderRadius: '50%',
        bgcolor: active ? t.palette.sacred.primary : 'transparent',
        transition: 'background-color 220ms',
      })}
    />
    {icon}
    <Typography sx={{
      fontFamily: 'var(--sacred-font-body)',
      fontSize: '0.66rem',
      fontWeight: active ? 700 : 500,
      lineHeight: 1,
    }}>
      {label}
    </Typography>
  </IconButton>
);

const Fab = ({ icon, label, onClick }) => (
  <Box sx={{ flex: 1, position: 'relative', display: 'flex', justifyContent: 'center' }}>
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); }}
      sx={(t) => ({
        position: 'absolute',
        top: -22,
        width: FAB_SIZE,
        height: FAB_SIZE,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        background: `linear-gradient(135deg, ${t.palette.sacred.primary} 0%, ${t.palette.sacred.secondary} 100%)`,
        color: t.palette.sacred.onPrimary,
        boxShadow: `0 8px 24px -6px ${t.palette.sacred.primary}66, 0 0 0 4px ${t.palette.sacred.surface}`,
        transition: 'transform 220ms cubic-bezier(0.16,1,0.3,1), box-shadow 220ms',
        '&:hover, &:focus': {
          transform: 'translateY(-2px)',
          boxShadow: `0 12px 32px -6px ${t.palette.sacred.primary}88, 0 0 0 4px ${t.palette.sacred.surface}`,
        },
        '&:active': {
          transform: 'translateY(0) scale(0.94)',
          boxShadow: `0 4px 12px -4px ${t.palette.sacred.primary}aa, 0 0 0 4px ${t.palette.sacred.surface}`,
        },
        '& svg': { fontSize: 26 },
      })}
      aria-label={label}
    >
      {icon}
    </Box>
    {/* Reserved space + label */}
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, pt: FAB_SIZE / 2 + 4 + 'px' }}>
      <Typography sx={{
        fontFamily: 'var(--sacred-font-body)',
        fontSize: '0.65rem',
        fontWeight: 700,
        color: 'sacred.primary',
        lineHeight: 1,
      }}>
        {label}
      </Typography>
    </Box>
  </Box>
);

/**
 * @param {Array<{ text, icon, path, isFab? }>} items — exactly 5 entries; one
 *   should have `isFab: true` (it becomes the raised center button).
 * @param {string} currentPath — location.pathname; used to mark `active`.
 * @param {(path: string) => void} onNavigate
 */
const MobileBottomNav = ({ items, currentPath, onNavigate }) => {
  useTheme(); // anchor the theme for sx callbacks above
  return (
    <Paper
      elevation={0}
      sx={(t) => ({
        display: { xs: 'block', md: 'none' },
        position: 'fixed', bottom: 0, left: 0, right: 0,
        zIndex: t.zIndex.appBar,
        borderRadius: 0,
        height: MOBILE_BAR_HEIGHT,
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        bgcolor: `${t.palette.sacred.surfaceContainerLow}E6`,
        borderTop: `1px solid ${t.palette.sacred.outlineVariant}33`,
        overflow: 'visible',  // let the FAB poke above
      })}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-end', height: '100%', px: 0.5 }}>
        {items.map((it) =>
          it.isFab ? (
            <Fab
              key={it.path}
              icon={it.icon}
              label={it.text}
              onClick={() => onNavigate(it.path)}
            />
          ) : (
            <NavItem
              key={it.path}
              icon={it.icon}
              label={it.text}
              active={currentPath.startsWith(it.path)}
              onClick={() => onNavigate(it.path)}
            />
          ),
        )}
      </Box>
    </Paper>
  );
};

export const MOBILE_BOTTOM_NAV_HEIGHT = MOBILE_BAR_HEIGHT;
export default MobileBottomNav;
